import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, ChevronLeft, Tag, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatIDR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showOffer, setShowOffer] = useState(false);
  const [offerAmt, setOfferAmt] = useState("");
  const bottomRef = useRef(null);

  const loadConvs = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setConvs(data);
    return data;
  }, []);

  const loadMessages = useCallback(async (cid) => {
    const { data } = await api.get(`/conversations/${cid}/messages`);
    setActive(data.conversation);
    setMessages(data.messages);
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  useEffect(() => {
    if (id) loadMessages(id);
    else setActive(null);
  }, [id, loadMessages]);

  useEffect(() => {
    if (!id) return;
    const t = setInterval(() => loadMessages(id), 4000);
    return () => clearInterval(t);
  }, [id, loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    const body = { text };
    setText("");
    await api.post(`/conversations/${id}/messages`, body);
    loadMessages(id);
    loadConvs();
  };

  const sendOffer = async () => {
    if (!offerAmt || Number(offerAmt) <= 0) return toast.error("Nominal tidak valid");
    await api.post(`/conversations/${id}/messages`, { offer_amount: Number(offerAmt) });
    setShowOffer(false);
    setOfferAmt("");
    loadMessages(id);
  };

  const respondOffer = async (mid, action) => {
    await api.post(`/messages/${mid}/offer`, { action });
    loadMessages(id);
    toast.success(action === "accept" ? "Penawaran diterima" : "Penawaran ditolak");
  };

  const ConvList = (
    <div className={`${id ? "hidden lg:block" : "block"} lg:col-span-4 border-r border-gray-100`}>
      <h1 className="text-xl font-extrabold text-gray-900 px-4 py-4">Pesan</h1>
      {convs.length === 0 && <div className="text-center text-gray-400 text-sm py-10">Belum ada percakapan.</div>}
      {convs.map((c) => (
        <button
          key={c.id}
          data-testid={`conversation-${c.id}`}
          onClick={() => navigate(`/chat/${c.id}`)}
          className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 ${active?.id === c.id ? "bg-[#EFF6FF]" : ""}`}
        >
          <img src={c.other_user.avatar_url} alt="" className="w-11 h-11 rounded-full object-cover" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-gray-900 truncate">{c.other_user.name}</div>
            <div className="text-xs text-gray-500 truncate">{c.last_message || c.product?.title}</div>
          </div>
          {c.product && <img src={fileUrl((c.product.images || [])[0])} alt="" className="w-10 h-10 rounded-lg object-cover" />}
        </button>
      ))}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden -mx-4 md:mx-0">
      <div className="grid grid-cols-1 lg:grid-cols-12 h-[calc(100vh-160px)]">
        {ConvList}

        <div className={`${id ? "block" : "hidden lg:flex lg:items-center lg:justify-center"} lg:col-span-8 flex flex-col`}>
          {!active ? (
            <div className="text-gray-400 text-sm text-center w-full">Pilih percakapan untuk mulai chat.</div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center gap-3 p-3 border-b border-gray-100">
                <button className="lg:hidden" onClick={() => navigate("/chat")}><ChevronLeft className="w-5 h-5 text-gray-500" /></button>
                <img src={active.other_user.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                <div className="flex-1">
                  <div className="font-semibold text-sm">{active.other_user.name}</div>
                  <div className="text-xs text-gray-400">{active.role === "seller" ? "Pembeli" : "Penjual"}</div>
                </div>
                {active.product && (
                  <div onClick={() => navigate(`/product/${active.product.id}`)} className="flex items-center gap-2 bg-gray-50 rounded-lg p-1.5 pr-3 cursor-pointer">
                    <img src={fileUrl((active.product.images || [])[0])} alt="" className="w-9 h-9 rounded object-cover" />
                    <div className="text-xs">
                      <div className="font-semibold text-[#0B4DA2]">{formatIDR(active.product.price)}</div>
                      <div className="text-gray-500 max-w-[120px] truncate">{active.product.title}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FAFAFA]">
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  if (m.offer) {
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className="bg-white border-2 border-[#DBEAFE] rounded-2xl p-3 max-w-[75%]">
                          <div className="flex items-center gap-1 text-xs text-[#0B4DA2] font-semibold"><Tag className="w-3 h-3" /> Penawaran Harga</div>
                          <div className="text-xl font-extrabold text-[#0B4DA2] my-1">{formatIDR(m.offer.amount)}</div>
                          {m.offer.status === "pending" && active.role === "seller" && !mine && (
                            <div className="flex gap-2 mt-2">
                              <button onClick={() => respondOffer(m.id, "accept")} className="flex-1 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Terima</button>
                              <button onClick={() => respondOffer(m.id, "reject")} className="flex-1 py-1.5 rounded-lg bg-gray-200 text-gray-700 text-xs font-semibold flex items-center justify-center gap-1"><X className="w-3 h-3" /> Tolak</button>
                            </div>
                          )}
                          {m.offer.status !== "pending" && (
                            <div className={`text-xs font-semibold ${m.offer.status === "accepted" ? "text-emerald-600" : "text-gray-400"}`}>
                              {m.offer.status === "accepted" ? "✅ Diterima" : "Ditolak"}
                            </div>
                          )}
                          {m.offer.status === "pending" && (mine || active.role !== "seller") && <div className="text-xs text-gray-400">Menunggu respon penjual...</div>}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`px-3.5 py-2 rounded-2xl max-w-[75%] text-sm ${mine ? "bg-[#0B4DA2] text-white rounded-br-sm" : "bg-white border border-gray-100 rounded-bl-sm"}`}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Quick replies + offer */}
              {active.role === "buyer" && (
                <div className="flex gap-2 px-3 pt-2 overflow-x-auto no-scrollbar">
                  {["Barang masih ada?", "Bisa nego?", "COD bisa?"].map((q) => (
                    <button key={q} onClick={() => { setText(q); }} className="shrink-0 text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-600">{q}</button>
                  ))}
                  <button data-testid="make-offer-button" onClick={() => setShowOffer(true)} className="shrink-0 text-xs px-3 py-1.5 rounded-full bg-[#EFF6FF] text-[#0B4DA2] font-semibold flex items-center gap-1"><Tag className="w-3 h-3" /> Buat Penawaran</button>
                </div>
              )}

              {showOffer && (
                <div className="px-3 py-2 flex items-center gap-2 border-t border-gray-100">
                  <input data-testid="offer-amount-input" type="number" value={offerAmt} onChange={(e) => setOfferAmt(e.target.value)} placeholder="Nominal penawaran" className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none" />
                  <button data-testid="send-offer-button" onClick={sendOffer} className="px-4 py-2 rounded-lg bg-[#0B4DA2] text-white text-sm font-semibold">Kirim</button>
                  <button onClick={() => setShowOffer(false)} className="p-2"><X className="w-4 h-4 text-gray-400" /></button>
                </div>
              )}

              {/* Input */}
              <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-gray-100">
                <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Ketik pesan..." className="flex-1 px-4 py-2.5 rounded-full bg-gray-100 outline-none text-sm" />
                <button data-testid="chat-send-message-button" type="submit" className="w-10 h-10 rounded-full bg-[#0B4DA2] text-white flex items-center justify-center"><Send className="w-4 h-4" /></button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
